import { useCallback, useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { Timer, LogIn, LogOut, Coffee, AlertTriangle, Pencil } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const toLocalInput = (iso) => { const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const KIND = { work: "Shift", break_unpaid: "Unpaid break", break_paid: "Paid break" };

export default function TimeClockPanel({ companyId, employees, start, end, canEdit, settings, onChanged }) {
  const [emp, setEmp] = useState("");
  const [data, setData] = useState({ punches: [], issues: [], open: {}, hours_by_date: {} });
  const [at, setAt] = useState("");
  const [paidBreak, setPaidBreak] = useState(false);
  const [fix, setFix] = useState(null);

  const clockEmps = employees.filter((e) => (e.time_entry_method || settings?.time_entry_method) === "time_clock");
  const list = clockEmps.length ? clockEmps : employees;

  const load = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/time-punches?start=${start}&end=${end}`).then((r) => setData(r.data)).catch(() => {});
  }, [companyId, start, end]);
  useEffect(() => { load(); }, [load]);

  const punch = async (action) => {
    if (!emp) { toast.error("Select an employee"); return; }
    try {
      const body = { employee_id: emp, action, paid_break: paidBreak, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
      if (at) body.at = new Date(at).toISOString();
      const { data: r } = await api.post(`/companies/${companyId}/time-punches`, body);
      toast.success(`${action.replace("_", " ")} recorded · ${r.day_hours.toFixed(2)}h on ${r.punch.work_date}`);
      setAt(""); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || apiErr(e)); }
  };
  const saveFix = async () => {
    if (!fix.reason.trim()) { toast.error("Reason for correction is required"); return; }
    try {
      const body = { reason: fix.reason, void: fix.void };
      if (fix.start_at) body.start_at = new Date(fix.start_at).toISOString();
      if (fix.end_at) body.end_at = new Date(fix.end_at).toISOString();
      await api.put(`/companies/${companyId}/time-punches/${fix.punch_id}`, body);
      toast.success("Punch corrected (original preserved in history)"); setFix(null); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || apiErr(e)); }
  };

  const open = emp ? data.open?.[emp] : null;
  const openBreak = emp ? data.punches.find((p) => p.employee_id === emp && p.kind !== "work" && !p.end_at && !p.voided) : null;
  const mine = data.punches.filter((p) => !emp || p.employee_id === emp);
  const name = (id) => { const e = employees.find((x) => x.employee_id === id); return e ? `${e.first_name} ${e.last_name}` : id; };
  const issuesFor = (pid) => data.issues.filter((i) => i.punch_id === pid);

  return (
    <div className="space-y-5" data-testid="time-clock-panel">
      <div className="border border-border bg-white p-5">
        <div className="flex items-center gap-2 mb-3"><Timer className="h-4 w-4" /><h3 className="text-sm font-bold uppercase tracking-widest">Time Clock</h3>
          {clockEmps.length === 0 && <span className="text-[10px] text-muted-foreground">No employee is set to Time Clock yet — showing all employees.</span>}</div>
        <div className="flex flex-wrap items-end gap-2">
          <select data-testid="clock-employee" value={emp} onChange={(e) => setEmp(e.target.value)} className="border border-input px-2 py-1.5 text-sm min-w-48">
            <option value="">Employee…</option>
            {list.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</option>)}
          </select>
          <label className="text-xs text-muted-foreground flex flex-col">Time (blank = now)
            <input data-testid="clock-at" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} className="border border-input px-2 py-1 text-sm" /></label>
          {canEdit && (!open ? (
            <button data-testid="clock-in" onClick={() => punch("clock_in")} className="inline-flex items-center gap-1 border border-black bg-black text-white px-3 py-1.5 text-sm font-medium hover:bg-white hover:text-black transition-colors"><LogIn className="h-4 w-4" /> Clock In</button>
          ) : (
            <>
              {!openBreak ? (
                <>
                  <label className="flex items-center gap-1 text-xs"><input data-testid="clock-paid-break" type="checkbox" checked={paidBreak} onChange={(e) => setPaidBreak(e.target.checked)} className="accent-black" /> paid break</label>
                  <button data-testid="break-start" onClick={() => punch("break_start")} className="inline-flex items-center gap-1 border border-border px-3 py-1.5 text-sm hover:border-black"><Coffee className="h-4 w-4" /> Start Break</button>
                </>
              ) : (
                <button data-testid="break-end" onClick={() => punch("break_end")} className="inline-flex items-center gap-1 border border-border px-3 py-1.5 text-sm hover:border-black"><Coffee className="h-4 w-4" /> End Break</button>
              )}
              <button data-testid="clock-out" onClick={() => punch("clock_out")} className="inline-flex items-center gap-1 border border-black bg-black text-white px-3 py-1.5 text-sm font-medium hover:bg-white hover:text-black transition-colors"><LogOut className="h-4 w-4" /> Clock Out</button>
            </>
          ))}
        </div>
        {emp && (
          <p className="mt-2 text-sm" data-testid="clock-status">
            {open ? <>Clocked in <span className="font-medium">{fmt(open.start_at)}</span>{openBreak ? <span className="ml-2 text-amber-700">· on {KIND[openBreak.kind].toLowerCase()} since {fmt(openBreak.start_at)}</span> : null}</> : <span className="text-muted-foreground">Not clocked in.</span>}
          </p>
        )}
      </div>

      {data.issues.length > 0 && (
        <div className="border border-amber-500 bg-amber-50 p-4 text-sm" data-testid="clock-issues">
          <div className="flex items-center gap-2 font-bold text-amber-800 uppercase text-xs tracking-widest mb-1"><AlertTriangle className="h-4 w-4" /> Needs Attention</div>
          {data.issues.filter((i) => !emp || mine.some((p) => p.punch_id === i.punch_id)).map((i, k) => <div key={k} className="text-amber-900">{i.date} · {i.detail}</div>)}
        </div>
      )}

      <div className="border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="punch-table">
          <thead><tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <th className="px-3 py-2">Employee</th><th className="px-3 py-2">Work date</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Start</th><th className="px-3 py-2">End</th><th className="px-3 py-2 text-right">Hours</th><th className="px-3 py-2">Flags</th><th className="px-3 py-2"></th></tr></thead>
          <tbody>
            {mine.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No punches this week.</td></tr>}
            {mine.map((p) => {
              const h = p.end_at ? (new Date(p.end_at) - new Date(p.start_at)) / 36e5 : null;
              const iss = issuesFor(p.punch_id);
              return (
                <tr key={p.punch_id} data-testid={`punch-${p.punch_id}`} className={`border-b border-border last:border-0 ${p.voided ? "opacity-40 line-through" : ""}`}>
                  <td className="px-3 py-2">{name(p.employee_id)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{p.work_date}</td>
                  <td className="px-3 py-2">{KIND[p.kind]}</td>
                  <td className="px-3 py-2 text-xs">{fmt(p.start_at)}</td>
                  <td className="px-3 py-2 text-xs">{p.end_at ? fmt(p.end_at) : <span className="text-amber-700 font-medium">open</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{h !== null ? h.toFixed(2) : "—"}</td>
                  <td className="px-3 py-2">
                    {iss.map((i, k) => <span key={k} title={i.detail} className="mr-1 text-[9px] font-mono uppercase border border-amber-500 bg-amber-50 text-amber-700 px-1 py-0.5">{i.kind.replace("_", " ")}</span>)}
                    {(p.corrections || []).length > 0 && <span title={p.corrections.map((c) => `${c.at.slice(0, 16)} ${c.by}: ${JSON.stringify(c.old)} → ${JSON.stringify(c.new)} (${c.reason})`).join("\n")} className="text-[9px] font-mono uppercase border border-violet-500 bg-violet-50 text-violet-700 px-1 py-0.5">corrected ×{p.corrections.length}</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && settings?.time_allow_manager_corrections && !p.voided && (
                      <button data-testid={`punch-fix-${p.punch_id}`} onClick={() => setFix({ punch_id: p.punch_id, start_at: toLocalInput(p.start_at), end_at: p.end_at ? toLocalInput(p.end_at) : "", reason: "", void: false })} className="h-7 w-7 inline-flex items-center justify-center border border-border hover:border-black" title="Correct punch"><Pencil className="h-3.5 w-3.5" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {fix && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="punch-fix-modal" onClick={() => setFix(null)}>
          <div className="bg-white border border-border w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold uppercase tracking-widest">Correct punch</h3>
            <label className="block text-xs">Start<input data-testid="fix-start" type="datetime-local" value={fix.start_at} onChange={(e) => setFix({ ...fix, start_at: e.target.value })} className="w-full border border-input px-2 py-1 text-sm" /></label>
            <label className="block text-xs">End<input data-testid="fix-end" type="datetime-local" value={fix.end_at} onChange={(e) => setFix({ ...fix, end_at: e.target.value })} className="w-full border border-input px-2 py-1 text-sm" /></label>
            <label className="flex items-center gap-2 text-xs"><input data-testid="fix-void" type="checkbox" checked={fix.void} onChange={(e) => setFix({ ...fix, void: e.target.checked })} className="accent-black" /> Void this punch (duplicate / mistake)</label>
            <label className="block text-xs">Reason for correction <span className="text-red-600">*</span>
              <input data-testid="fix-reason" value={fix.reason} onChange={(e) => setFix({ ...fix, reason: e.target.value })} placeholder="e.g. Employee forgot to clock out" className="w-full border border-input px-2 py-1 text-sm" /></label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setFix(null)} className="border border-border px-3 py-1.5 text-sm hover:border-black">Cancel</button>
              <button data-testid="fix-save" onClick={saveFix} className="border border-black bg-black text-white px-4 py-1.5 text-sm font-medium hover:bg-white hover:text-black transition-colors">Save correction</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
